import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, GripVertical, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const inputCls = 'w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]';
const bool = (v) => v === true || v === 'true' || v === 'True';
const TYPE_LABELS = { text: 'Metin', textarea: 'Uzun Metin', date: 'Tarih', select: 'Seçim', image: 'Görsel', tel: 'Telefon', email: 'E-posta' };

export default function MemberFieldManager() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: '', field_type: 'text', is_required: false, options: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchFields = useCallback(() => {
    setLoading(true);
    api.get('/admin/member-fields').then(r => setFields(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { fetchFields(); }, [fetchFields]);

  const patch = async (id, data) => {
    try { await api.put(`/admin/member-fields/${id}`, data); fetchFields(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Güncelleme hatası'); }
  };

  const addField = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) { toast.error('Alan adı gerekli'); return; }
    setIsSubmitting(true);
    try {
      const options = form.field_type === 'select' ? form.options.split(',').map(s => s.trim()).filter(Boolean) : [];
      await api.post('/admin/member-fields', { label: form.label, field_type: form.field_type, is_required: form.is_required, options, position: fields.length + 1 });
      toast.success('Alan eklendi');
      setForm({ label: '', field_type: 'text', is_required: false, options: '' });
      setShowForm(false);
      fetchFields();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata oluştu'); }
    finally { setIsSubmitting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Bu özel alanı silmek istediğinize emin misiniz?')) return;
    try { await api.delete(`/admin/member-fields/${id}`); toast.success('Silindi'); fetchFields(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Silme hatası'); }
  };

  return (
    <div data-testid="admin-member-field-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Üye Alanları</h1>
          <p className="text-sm text-muted-foreground mt-1">Profil bilgisi alanlarını zorunlu/opsiyonel yapın veya yeni alan ekleyin</p>
        </div>
        <button onClick={() => setShowForm(s => !s)} data-testid="add-field-toggle"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)]">
          <Plus className="w-4 h-4" /> Yeni Alan
        </button>
      </div>

      {showForm && (
        <form onSubmit={addField} className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)] space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Alan Adı *</label>
              <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} className={inputCls} placeholder="ör. Kan Grubu" data-testid="field-label" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Alan Tipi</label>
              <select value={form.field_type} onChange={e => setForm(f => ({ ...f, field_type: e.target.value }))} className={inputCls} data-testid="field-type">
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          {form.field_type === 'select' && (
            <div>
              <label className="block text-sm font-medium mb-1.5">Seçenekler (virgülle ayırın)</label>
              <input value={form.options} onChange={e => setForm(f => ({ ...f, options: e.target.value }))} className={inputCls} placeholder="A Rh+, A Rh-, 0 Rh+" />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_required} onChange={e => setForm(f => ({ ...f, is_required: e.target.checked }))} className="accent-[hsl(var(--gold))]" /> Zorunlu alan
          </label>
          <div className="flex gap-3">
            <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50" data-testid="field-save">
              {isSubmitting ? 'Ekleniyor...' : 'Ekle'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="rounded-2xl bg-card border border-border overflow-hidden">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 px-5 py-3 border-b border-border text-xs font-medium text-muted-foreground uppercase tracking-wide">
            <span>Alan</span><span className="text-center">Zorunlu</span><span className="text-center">Görünür</span><span></span>
          </div>
          {fields.map(f => (
            <div key={f.id} className="grid grid-cols-[1fr_auto_auto_auto] gap-4 px-5 py-3 border-b border-border last:border-0 items-center" data-testid="field-row">
              <div className="flex items-center gap-2 min-w-0">
                <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{f.label} {bool(f.is_system) && <span className="text-[10px] text-muted-foreground">(sistem)</span>}</div>
                  <div className="text-xs text-muted-foreground">{TYPE_LABELS[f.field_type] || f.field_type}</div>
                </div>
              </div>
              <label className="flex justify-center">
                <input type="checkbox" checked={bool(f.is_required)} onChange={e => patch(f.id, { is_required: e.target.checked })} className="accent-[hsl(var(--gold))]" data-testid="field-required-toggle" />
              </label>
              <label className="flex justify-center">
                <input type="checkbox" checked={bool(f.is_enabled)} onChange={e => patch(f.id, { is_enabled: e.target.checked })} className="accent-[hsl(var(--gold))]" data-testid="field-enabled-toggle" />
              </label>
              <div className="flex justify-end">
                {!bool(f.is_system) && (
                  <button onClick={() => remove(f.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
